import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { Card } from "@/components/ui/Card";
import { Skeleton, Spinner } from "@/components/ui/Spinner";
import { Business, Projects, Tasks } from "@/api/resources";
import { TaskCard } from "@/components/TaskCard";
import { TaskSyncStatus } from "@/components/TaskSyncStatus";
import { liveTaskQueryOptions } from "@/boot/query-sync";
import { InlineText } from "@/components/InlineText";
import { InlineDate } from "@/components/InlineDate";
import { ProgressBadge } from "@/components/ProgressBadge";
import { SourceIdeas } from "@/components/IdeaWork";
import { PassportCard } from "@/components/Passport";
import { ResourcesSection } from "@/components/Resources";

/**
 * Project detail = horizontally-scrollable Kanban columns matching
 * project_statuses order. Each column has its own vertical scroll.
 */
export function ProjectView() {
  const { project_id } = useParams<{ project_id: string }>();
  const queryClient = useQueryClient();
  const project = useQuery({
    queryKey: ["project", project_id],
    queryFn: () => Projects.get(project_id!),
    enabled: Boolean(project_id),
  });

  // Spec 018: the sections a passport is written to, for an empty one.
  const templates = useQuery({
    queryKey: ["passport-templates"],
    queryFn: Business.templates,
    staleTime: 3_600_000,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["project", project_id] });
    void queryClient.invalidateQueries({ queryKey: ["projects"] });
  };

  // SPEC-2 §7 — the version this screen read goes with every edit, so a
  // partner's concurrent change is refused rather than silently overwritten.
  const saveSummary = async (next: string | null) => {
    if (!project_id || project.data == null) return;
    await Projects.update(project_id, { summary: next, version: project.data.version });
    invalidate();
  };

  const saveTarget = async (next: string | null) => {
    if (!project_id || project.data == null) return;
    await Projects.update(project_id, {
      target_completion_at: next,
      version: project.data.version,
    });
    invalidate();
  };
  const statuses = useQuery({
    queryKey: ["project-statuses", project_id],
    queryFn: () => Projects.statuses(project_id!),
    enabled: Boolean(project_id),
  });
  const tasks = useQuery({
    queryKey: ["project-tasks", project_id],
    queryFn: () => Tasks.listInProject(project_id!),
    enabled: Boolean(project_id),
    ...liveTaskQueryOptions,
  });

  if (!project_id) return null;
  if (project.isLoading || statuses.isLoading) return <Spinner />;
  if (project.isError) return <Card><p>Проект не найден.</p></Card>;

  const sortedStatuses = (statuses.data ?? []).slice().sort((a, b) => a.order - b.order);
  const tasksByStatus = new Map<string, typeof tasks.data>();
  for (const t of tasks.data ?? []) {
    if (t.parent_task_id) continue; // hide subtasks at top level for now
    const list = tasksByStatus.get(t.status_id) ?? [];
    list.push(t);
    tasksByStatus.set(t.status_id, list);
  }

  return (
    <div className="-mx-4 space-y-3">
      <div className="space-y-2 px-4">
        <div className="flex items-start justify-between gap-2">
          <h1 className="text-xl font-semibold">{project.data?.title}</h1>
          {project.data && (
            <ProgressBadge done={project.data.tasks_done} total={project.data.task_count} />
          )}
        </div>
        {project.data && (
          <>
            <InlineText
              value={project.data.summary}
              ariaLabel="Краткое описание проекта"
              placeholder="Кратко: о чём этот проект"
              maxLength={240}
              onCommit={async (v) => {
                try {
                  await saveSummary(v);
                  return true;
                } catch {
                  return false;
                }
              }}
            />
            <InlineDate
              value={project.data.target_completion_at}
              ariaLabel="Дедлайн проекта"
              placeholder="Поставить дедлайн"
              onCommit={async (v) => {
                try {
                  await saveTarget(v);
                  return true;
                } catch {
                  return false;
                }
              }}
            />
          </>
        )}
      </div>
      {project.data && (
        <div className="px-4">
          <PassportCard
            title="Паспорт проекта"
            body={project.data.description_md}
            version={project.data.version}
            bodyKey="description_md"
            template={templates.data?.project ?? ""}
            freshness={project.data.passport_freshness}
            checkedAt={project.data.passport_checked_at}
            checkedBy={project.data.passport_checked_by_name}
            historyKey={["project-passport-revisions", project_id]}
            loadHistory={() => Projects.passportRevisions(project_id)}
            onSave={async (body, source, version) => {
              await Projects.update(project_id, { description_md: body, source_md: source, version });
              invalidate();
              void queryClient.invalidateQueries({ queryKey: ["project-passport-revisions", project_id] });
            }}
            onCheck={async () => {
              await Projects.checkPassport(project_id, project.data.version);
              invalidate();
            }}
            onRefresh={invalidate}
          />
        </div>
      )}
      <div className="px-4"><ResourcesSection title="Ресурсы проекта" projectId={project_id} /></div>
      <div className="px-4"><SourceIdeas kind="project" id={project_id} /></div>
      <div className="px-4"><TaskSyncStatus query={tasks} /></div>
      {!tasks.isError && <div className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-4 pl-4 pr-4">
        {sortedStatuses.map((s) => {
          const items = tasksByStatus.get(s.id) ?? [];
          return (
            <section
              key={s.id}
              className="flex w-[78vw] min-w-[260px] max-w-[320px] shrink-0 snap-start flex-col gap-2 rounded-card bg-sbg p-3"
              aria-label={`Колонка ${s.name}`}
            >
              <header className="flex items-center justify-between">
                <span className="text-sm font-medium">{s.name}</span>
                <span className="text-xs text-hint">{items.length}</span>
              </header>
              <div className="flex flex-col gap-2">
                {tasks.isLoading && <Skeleton className="h-16 w-full" />}
                {items.length === 0 && tasks.isFetched && (
                  <span className="px-1 text-xs text-hint">пусто</span>
                )}
                {items.map((t) => (
                  <TaskCard key={t.id} task={t} />
                ))}
              </div>
            </section>
          );
        })}
      </div>}
    </div>
  );
}
